import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Check, Search, X } from "lucide-react";
import { Field, SearchableInput, Recap } from "@/pages/register/Fields";
import { PersonalStep, CredentialsStep, validatePersonal, validateCredentials } from "@/pages/register/CommonSteps";
import { IaIefFields } from "@/pages/register/DirecteurForm";
import { REGIONS, LEVELS, SCHOOL_TYPES } from "@/data/senegal";

const ORGS = [
  { value: "simple", label: "Classe simple", desc: "Une classe par niveau" },
  { value: "multigrade", label: "Multigrade", desc: "Plusieurs niveaux : une classe distincte par niveau" },
  { value: "double_flux", label: "Double flux", desc: "Plusieurs groupes qui se relaient" },
];
const TEACHER_TYPES = [
  ["independent", "Enseignant indépendant", "Je gère moi-même mon école et mes classes"],
  ["attached", "Rattaché à un directeur", "Je travaille dans une école déjà inscrite par son directeur"],
];
const typeLabel = (v) => SCHOOL_TYPES.find((t) => t.value === v)?.label;

function DirectorPicker({ school, setSchool }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/public/directors", { params: { q } }).then(({ data }) => setResults(data)).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q]);

  if (school.school_id) {
    const rows = [["École", school.nom], ["Directeur", school.directeur], ["IA", school.ia], ["IEF", school.ief], ["Région", school.region], ["Localité", school.localite], ["Type", typeLabel(school.type_etablissement)], ["Année scolaire", school.annee_scolaire]];
    return (
      <div className="rounded-xl border-2 border-[#1F7A63] bg-[#E8F2EF] p-3" data-testid="reg-school-selected">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-5 h-5 text-[#1F7A63]" />
          <span className="font-semibold text-sm flex-1">Établissement sélectionné (lecture seule)</span>
          <button type="button" onClick={() => setSchool({ school_id: null, nom: "" })} className="p-1.5 text-[#5C736A]" aria-label="Changer" data-testid="reg-school-change"><X className="w-4 h-4" /></button>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {rows.map(([k, v]) => <div key={k}><dt className="text-[#5C736A]">{k}</dt><dd className="font-semibold">{v || "—"}</dd></div>)}
        </dl>
        <p className="text-xs text-[#5C736A] mt-2">Le rattachement sera confirmé par le directeur, puis le compte validé par l&apos;administrateur.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
        <Input className="h-12 pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Directeur, école, IA ou IEF…" data-testid="reg-director-search" />
      </div>
      <div className="rounded-xl border border-[#E2E8F0] bg-white divide-y divide-[#E2E8F0] max-h-72 overflow-y-auto" data-testid="reg-director-results">
        {results.map((s) => (
          <button type="button" key={s.school_id} onClick={() => setSchool({ ...s, mode: "existing" })}
            className="w-full text-left px-3 py-3 hover:bg-[#E8F2EF] transition-colors" data-testid="reg-director-result">
            <div className="text-sm font-semibold">{s.nom}</div>
            <div className="text-xs text-[#5C736A]">Dir. {s.directeur} · {[s.ia, s.ief].filter(Boolean).join(" · ")}</div>
          </button>
        ))}
        {!results.length && <div className="px-3 py-4 text-sm text-[#5C736A]" data-testid="reg-director-empty">Aucun directeur trouvé. Choisissez un autre établissement, ou demandez au directeur de s&apos;inscrire d&apos;abord, ou inscrivez-vous comme enseignant indépendant.</div>}
      </div>
    </div>
  );
}

function LevelsPicker({ form, set }) {
  const toggle = (lv) => {
    const has = form.niveaux.includes(lv);
    set({ niveaux: has ? form.niveaux.filter((x) => x !== lv) : [...form.niveaux, lv].sort((a, b) => LEVELS.indexOf(a) - LEVELS.indexOf(b)) });
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="reg-levels">
      {LEVELS.map((lv) => {
        const on = form.niveaux.includes(lv);
        return (
          <button type="button" key={lv} onClick={() => toggle(lv)} data-testid={`reg-level-${lv}`}
            className={`h-11 min-w-[64px] px-4 rounded-full border text-sm font-semibold transition-colors ${on ? "bg-[#1F7A63] border-[#1F7A63] text-white" : "bg-white border-[#E2E8F0] hover:border-[#1F7A63]"}`}>
            {on && <Check className="w-3.5 h-3.5 inline mr-1" />}{lv}
          </button>
        );
      })}
    </div>
  );
}

function ChoiceCards({ items, value, onPick, prefix, cols = "sm:grid-cols-3" }) {
  return (
    <div className={`grid grid-cols-1 ${cols} gap-2`}>
      {items.map(([v, l, d]) => (
        <button type="button" key={v} onClick={() => onPick(v)} data-testid={`${prefix}-${v}`}
          className={`text-left p-3 rounded-xl border-2 transition-colors ${value === v ? "border-[#1F7A63] bg-[#E8F2EF]" : "border-[#E2E8F0] bg-white hover:border-[#1F7A63]/50"}`}>
          <div className="font-semibold text-sm">{l}</div><div className="text-xs text-[#5C736A]">{d}</div>
        </button>
      ))}
    </div>
  );
}

function IndependentSchool({ s, setSchool }) {
  return (
    <>
      <Field label="Nom de l'école" required className="sm:col-span-2"><Input className="h-12" value={s.nom} onChange={(e) => setSchool({ nom: e.target.value })} data-testid="reg-school-nom" /></Field>
      <Field label="Type d'établissement" required>
        <Select value={s.type_etablissement} onValueChange={(v) => setSchool({ type_etablissement: v })}>
          <SelectTrigger className="h-12" data-testid="reg-school-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
          <SelectContent>{SCHOOL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Année scolaire" required><Input className="h-12" value={s.annee_scolaire} onChange={(e) => setSchool({ annee_scolaire: e.target.value })} placeholder="2025-2026" data-testid="reg-annee" /></Field>
      <IaIefFields school={s} setSchool={setSchool} />
      <Field label="Région" required><SearchableInput id="t-region-list" options={REGIONS} value={s.region} onChange={(v) => setSchool({ region: v })} testid="reg-region" /></Field>
      <Field label="Localité"><Input className="h-12" value={s.localite} onChange={(e) => setSchool({ localite: e.target.value })} data-testid="reg-localite" /></Field>
    </>
  );
}

function SchoolStep({ form, set }) {
  const s = form.school;
  const setSchool = (patch) => set({ school: { ...s, ...patch } });
  const chooseType = (t) => set({ teacher_type: t, school: { ...s, mode: t === "attached" ? "existing" : "new", school_id: null, nom: "", ia: "", ief: "", region: "", localite: "", type_etablissement: "" } });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Votre statut" required className="sm:col-span-2">
        <ChoiceCards items={TEACHER_TYPES} value={form.teacher_type} onPick={chooseType} prefix="reg-type" cols="sm:grid-cols-2" />
      </Field>
      {form.teacher_type === "attached" && <Field label="Directeur et établissement" required className="sm:col-span-2"><DirectorPicker school={s} setSchool={setSchool} /></Field>}
      {form.teacher_type === "independent" && <IndependentSchool s={s} setSchool={setSchool} />}
      {form.teacher_type && (
        <>
          <Field label="Niveau(x) ou classe(s) enseigné(s)" required className="sm:col-span-2" hint="Vous pouvez en sélectionner plusieurs."><LevelsPicker form={form} set={set} /></Field>
          <Field label="Organisation pédagogique" required className="sm:col-span-2">
            <ChoiceCards items={ORGS.map((o) => [o.value, o.label, o.desc])} value={form.organisation} onPick={(v) => set({ organisation: v })} prefix="reg-org" />
          </Field>
          {form.organisation === "double_flux" && (
            <Field label="Groupes ou flux gérés" className="sm:col-span-2" hint="Séparez par des virgules, ex : A, B">
              <Input className="h-12" value={form.flux} onChange={(e) => set({ flux: e.target.value })} placeholder="A, B" data-testid="reg-flux" />
            </Field>
          )}
        </>
      )}
    </div>
  );
}

const orgLabel = (v) => ORGS.find((o) => o.value === v)?.label;

export const enseignantForm = {
  steps: ["Vous", "Votre école", "Identifiants", "Envoi"],
  render(step, form, set) {
    if (step === 0) return <PersonalStep form={form} set={set} />;
    if (step === 1) return <SchoolStep form={form} set={set} />;
    if (step === 2) return <CredentialsStep form={form} set={set} />;
    const s = form.school;
    return (
      <Recap rows={[
        ["Prénom et nom", `${form.prenom} ${form.nom}`], ["Sexe", form.sexe === "F" ? "Féminin" : "Masculin"],
        ["Téléphone", form.telephone], ["E-mail", form.email],
        ["Statut", form.teacher_type === "attached" ? `Rattaché à ${s.directeur || "un directeur"}` : "Enseignant indépendant"],
        ["École", s.nom], ["Type", typeLabel(s.type_etablissement)], ["IA", s.ia], ["IEF", s.ief], ["Région", s.region], ["Localité", s.localite],
        ["Année scolaire", s.annee_scolaire],
        ["Niveaux", form.niveaux.join(", ")], ["Organisation", orgLabel(form.organisation)],
        ...(form.organisation === "double_flux" ? [["Flux", form.flux || "A, B"]] : []),
        ["Identifiant", form.username],
      ]} />
    );
  },
  validate(step, f) {
    if (step === 0) return validatePersonal(f, false);
    if (step === 1) {
      const s = f.school;
      if (!f.teacher_type) return "Indiquez si vous êtes indépendant ou rattaché à un directeur.";
      if (f.teacher_type === "attached" && !s.school_id) return "Sélectionnez votre directeur et son établissement.";
      if (f.teacher_type === "independent") {
        if ((s.nom || "").trim().length < 2) return "Indiquez le nom de votre école.";
        if (!s.type_etablissement) return "Choisissez le type d'établissement.";
        if (!s.annee_scolaire?.trim()) return "Indiquez l'année scolaire.";
        if (!s.region?.trim() || !s.ia?.trim() || !s.ief?.trim()) return "Renseignez la région, l'IA et l'IEF.";
      }
      if (!f.niveaux.length) return "Sélectionnez au moins un niveau.";
      if (!f.organisation) return "Choisissez l'organisation pédagogique.";
      if (f.organisation === "multigrade" && f.niveaux.length < 2) return "En multigrade, sélectionnez au moins deux niveaux.";
      return null;
    }
    if (step === 2) return validateCredentials(f);
    return null;
  },
  submitLabel: "Demander l'activation de mon compte",
  successText: "Votre inscription a été reçue. Votre compte sera accessible après validation par l'administrateur EDUKATI.",
};
